import {DependencyList, useEffect, useRef, useState} from 'react'

import noop from 'lodash/noop'

import {CancellablePromise, PromiseCancelledError, ensureCancelablePromise} from '../libs/promise'

export interface UseAsyncState<Data = unknown> {
  isLoading: boolean
  data?: Data
  error?: Error
}

interface UseAsyncOptions<Data> {
  onError?: (error: Error) => void
  onDone?: (data: Data) => void
  isEnabled?: boolean
  defaultLoading?: boolean
}

export default <Data = unknown>(
  handleAsync: () => Promise<Data> | CancellablePromise<Data>,
  deps: DependencyList = [],
  {onError = noop, onDone = noop, isEnabled = true, defaultLoading = false}: UseAsyncOptions<Data>
) => {
  const currentCancel = useRef<() => void>()
  const [state, setState] = useState<UseAsyncState<Data>>({
    isLoading: defaultLoading,
  })

  const cancelLastJob = () => {
    if (currentCancel.current) {
      currentCancel.current()
      currentCancel.current = undefined
    }
  }

  const startJob = () => {
    cancelLastJob()

    const {promise, cancel} = ensureCancelablePromise(handleAsync())
    currentCancel.current = cancel

    setState(value => ({
      ...value,
      isLoading: true,
      error: undefined,
    }))

    return promise
      .then(data => {
        setState(value => ({
          ...value,
          isLoading: false,
          data,
        }))
        onDone(data)
      })
      .catch(error => {
        if (error instanceof PromiseCancelledError) {
          setState(value => ({
            ...value,
            isLoading: false,
            error: undefined,
          }))
        } else {
          onError(error)
          setState(value => ({
            ...value,
            isLoading: false,
            error,
          }))
        }
      })
  }

  useEffect(() => {
    if (isEnabled) {
      startJob()
    }
  }, [isEnabled, ...deps])

  useEffect(() => () => cancelLastJob(), [])

  return {
    ...state,
    refresh: startJob,
  }
}
